import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { formsApi, pipedriveApi } from "../../services/api";
import {
  FormCreateRequest,
  FormUpdateRequest,
  FormVariation,
  CustomField,
  PipedriveProduct,
  PipedriveProductVariation,
} from "../../types";
import {
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "../../contexts/TranslationContext";
import {
  dateToLocalDateTimeString,
  localDateTimeStringToDate,
  prepareFormDataForSubmission,
} from "../../utils/dateUtils";

interface FormBuilderData extends FormCreateRequest {
  variations: FormVariation[];
  custom_fields: CustomField[];
}

const FormBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const formId = id ? Number(id) : undefined;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [hasInitialized, setHasInitialized] = useState(false);
  const isEditMode = !!formId;

  // Fetch existing form data if in edit mode
  const { isLoading: isLoadingForm } = useQuery(
    ["form", formId],
    () => formsApi.getForm(formId!),
    {
      enabled: isEditMode,
      onSuccess: (form) => {
        if (hasInitialized) {
          return;
        }

        // Populate form with existing data, converting UTC strings to Date objects
        // const formWithDates = prepareFormDataFromAPI(form);
        setValue("name", form.name);
        setValue("description", form.description || "");
        setValue("organization", form.organization || "TATO_NET");
        setValue("rules_label", form.rules_label || "");
        setValue("rules_url", form.rules_url || "");
        const mappedCustomFields: CustomField[] = (
          form.custom_fields || []
        ).map((field) => ({
          name: field.name || "",
          label: field.label || "",
          pipedrive_deal_field_code: field.pipedrive_deal_field_code ?? "",
          required: field.required || false,
        }));
        setValue("custom_fields", mappedCustomFields);
        setValue("pipedrive_product_id", form.pipedrive_product_id);
        setValue("scholarship_enabled", form.scholarship_enabled || false);
        setValue("custom_price_enabled", form.custom_price_enabled || false);
        setValue("promo_codes_enabled", form.promo_codes_enabled || false);
        setValue("variations", form.variations);
        setValue("css", form.css);

        // Set selected product if available
        if (form.pipedrive_product_id) {
          pipedriveApi
            .getProduct(form.pipedrive_product_id)
            .then((product) => {
              setSelectedProduct(product);
              return pipedriveApi.getProductVariations(product.id);
            })
            .then((variations) => {
              setAvailableVariations(variations);
            })
            .catch((error) => {
              console.error("Error fetching product data:", error);
            });
        }

        setHasInitialized(true);
      },
    },
  );

  // Product search state
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] =
    useState<PipedriveProduct | null>(null);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [availableVariations, setAvailableVariations] = useState<
    PipedriveProductVariation[]
  >([]);

  // Variation creation state
  const [showVariationSelector, setShowVariationSelector] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormBuilderData>({
    defaultValues: {
      name: "",
      description: "",
      organization: "TATO_NET",
      fields: ["firstName", "lastName", "email", "phone"],
      custom_fields: [],
      pipedrive_product_id: 0,
      scholarship_enabled: false,
      custom_price_enabled: false,
      promo_codes_enabled: false,
      rules_label: "",
      rules_url: "",
      variations: [],
      css: `/* Custom CSS for your form */`,
    },
  });

  const {
    fields: variationFields,
    append: appendVariation,
    remove: removeVariation,
    replace: replaceVariations,
  } = useFieldArray({
    control,
    name: "variations",
  });

  const {
    fields: customFieldsArray,
    append: appendCustomField,
    remove: removeCustomField,
  } = useFieldArray({
    control,
    name: "custom_fields",
  });

  // Product search query
  const { data: searchResults = [] } = useQuery(
    ["pipedrive-products", productSearchTerm],
    () => pipedriveApi.searchProducts(productSearchTerm),
    {
      enabled: productSearchTerm.length > 0,
      staleTime: 30000, // 30 seconds
    },
  );

  // Pipedrive deal fields query
  const { data: dealFields = [] } = useQuery(
    ["pipedrive-deal-fields"],
    () => pipedriveApi.getDealFields(),
    {
      staleTime: 300000, // 5 minutes
    },
  );

  // Fetch variations when product is selected
  useEffect(() => {
    if (selectedProduct?.id) {
      pipedriveApi
        .getProductVariations(selectedProduct.id)
        .then((variations) => {
          setAvailableVariations(variations);
        })
        .catch((error) => {
          console.error("Error fetching variations:", error);
          setAvailableVariations([]);
        });
    } else {
      setAvailableVariations([]);
      replaceVariations([]);
    }
  }, [selectedProduct, replaceVariations]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Don't close if clicking inside the dropdown or search input
      if (showProductDropdown && !target.closest(".product-search-container")) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProductDropdown]);

  const createFormMutation = useMutation(formsApi.createForm, {
    onSuccess: (data) => {
      queryClient.invalidateQueries("forms");
      navigate(`/forms/${data.id}`);
    },
  });

  const updateFormMutation = useMutation(
    (data: FormUpdateRequest) => formsApi.updateForm(formId!, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("forms");
        queryClient.invalidateQueries(["form", formId]);
        navigate(`/forms/${data.id}`);
      },
    },
  );

  const onSubmit = async (data: FormBuilderData) => {
    // Validate that at least one variation is added
    if (!data.variations || data.variations.length === 0) {
      alert(
        "Please add at least one product variation before saving the form.",
      );
      return;
    }

    try {
      // Convert Date objects to UTC ISO strings for backend submission
      const submissionData = prepareFormDataForSubmission(data);

      if (submissionData.custom_fields) {
        submissionData.custom_fields = submissionData.custom_fields.map(
          (field: CustomField) => ({
            ...field,
            pipedrive_deal_field_code:
              field.pipedrive_deal_field_code || (null as any),
          }),
        );
      }

      if (isEditMode) {
        await updateFormMutation.mutateAsync(submissionData);
      } else {
        await createFormMutation.mutateAsync(submissionData);
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} form:`,
        error,
      );
    }
  };

  // Product selection handlers
  const handleProductSelect = (product: PipedriveProduct) => {
    pipedriveApi
      .getProduct(product.id)
      .then((product) => {
        setSelectedProduct(product);
        replaceVariations([
          {
            name: `${product.name}`,
            stripe_price_id: "",
            pipedrive_variation_id: -1,
            price: product.prices?.[0]?.price || 0,
            expires_at: null,
          },
        ]);
      })
      .catch((error) => {
        console.error("Error fetching product:", error);
        setSelectedProduct(product);
      });

    setProductSearchTerm(product.name);
    setShowProductDropdown(false);
    setValue("pipedrive_product_id", product.id);

    // Auto-fill form name if it's empty
    const currentName = watch("name");
    if (!currentName || currentName.trim() === "") {
      setValue("name", product.name);
    }
  };

  const handleAddVariation = (variation: PipedriveProductVariation) => {
    appendVariation({
      name: variation.name,
      stripe_price_id: "",
      pipedrive_variation_id: variation.id,
      price: variation.prices?.[0]?.price || 0,
      expires_at: null,
    });
  };

  // Helper function to generate internal field name from label
  const generateFieldName = (label: string): string => {
    if (!label) return "";

    // Convert to lowercase, replace spaces and special chars with underscores
    let fieldName = label
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");

    // Ensure it starts with a letter
    if (fieldName && !/^[a-z]/.test(fieldName)) {
      fieldName = "field_" + fieldName;
    }

    // Add a small hash to avoid collisions
    const hash = Math.random().toString(36).substring(2, 6);
    return fieldName ? `${fieldName}_${hash}` : `custom_field_${hash}`;
  };

  const handleAddCustomField = () => {
    appendCustomField({
      name: "",
      label: "",
      pipedrive_deal_field_code: "",
      required: false,
    });
  };

  const watchedData = watch();

  // Auto-generate internal field names when labels change
  useEffect(() => {
    customFieldsArray.forEach((field, index) => {
      const currentLabel = watch(`custom_fields.${index}.label`);
      const currentName = watch(`custom_fields.${index}.name`);

      if (currentLabel && !currentName) {
        const generatedName = generateFieldName(currentLabel);
        setValue(`custom_fields.${index}.name`, generatedName);
      }
    });
  }, [
    JSON.stringify(
      customFieldsArray.map((_, index) =>
        watch(`custom_fields.${index}.label`),
      ),
    ),
  ]);

  // Update preview when form data changes
  useEffect(() => {
    const updatePreview = async () => {
      try {
        const formData = watchedData;

        // Show placeholder until product is selected
        if (!formData.variations?.length) {
          setPreviewHtml(`
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: #666; font-family: Arial, sans-serif; text-align: center; padding: 20px;">
              <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">📋</div>
              <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: 500;">Select a Product First</h3>
              <p style="margin: 0; font-size: 14px; opacity: 0.7;">Choose a product from Pipedrive to see your form preview</p>
            </div>
          `);
          return;
        }

        // Show basic preview if we have product but no other data
        if (!formData.name && !formData.fields?.length) {
          setPreviewHtml(`
            <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-family: Arial, sans-serif;">
              <p>Start building your form to see the preview</p>
            </div>
          `);
          return;
        }

        const mappedCustomFields = (formData.custom_fields || []).map(
          (field: CustomField) => ({
            ...field,
            pipedrive_deal_field_code:
              field.pipedrive_deal_field_code || (null as any),
          }),
        );

        const previewData = {
          ...formData,
          name: formData.name || "Form Preview",
          fields: formData.fields?.length
            ? formData.fields
            : ["firstName", "lastName", "email", "phone"],
          custom_fields: mappedCustomFields,
          variations: formData.variations || [],
        };

        const html = await formsApi.getFormPreview(previewData);
        setPreviewHtml(html);
      } catch (error) {
        console.error("Preview error:", error);
        setPreviewHtml(`
          <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #dc3545; font-family: Arial, sans-serif;">
            <p>Error loading preview</p>
          </div>
        `);
      }
    };

    updatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedData.name,
    watchedData.description,
    watchedData.organization,
    watchedData.fields,
    watchedData.custom_fields,
    watchedData.variations,
    watchedData.css,
    watchedData.scholarship_enabled,
    watchedData.custom_price_enabled,
    watchedData.promo_codes_enabled,
    watchedData.rules_label,
    watchedData.rules_url,
    // Watch for changes in individual variation properties
    JSON.stringify(
      watchedData.variations?.map((v) => ({
        name: v.name,
        price: v.price,
        stripe_price_id: v.stripe_price_id,
        expires_at: v.expires_at,
      })),
    ),
    // Watch for changes in custom fields
    JSON.stringify(
      watchedData.custom_fields?.map((cf) => ({
        name: cf.name,
        label: cf.label,
        pipedrive_deal_field_code: cf.pipedrive_deal_field_code,
        required: cf.required,
      })),
    ),
  ]);

  // Show loading state while fetching form data in edit mode
  if (isEditMode && isLoadingForm) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {isEditMode
              ? t("forms.builder.editTitle")
              : t("forms.builder.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {isEditMode
              ? t("forms.builder.editSubtitle")
              : t("forms.builder.subtitle")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Builder */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t("forms.builder.basicInfo.title")}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t("forms.builder.basicInfo.formName")}
                    </label>
                    <input
                      type="text"
                      {...register("name", {
                        required: "Form name is required",
                      })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder={t(
                        "forms.builder.basicInfo.formNamePlaceholder",
                      )}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  {/* Form Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t("forms.builder.basicInfo.formDescription")}
                    </label>
                    <textarea
                      {...register("description")}
                      rows={4}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder={t(
                        "forms.builder.basicInfo.formDescriptionPlaceholder",
                      )}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t("forms.builder.basicInfo.formDescriptionHelp")}
                    </p>
                  </div>

                  {/* Organization */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t("forms.builder.basicInfo.organization")}
                    </label>
                    <select
                      {...register("organization", {
                        required: t(
                          "forms.builder.basicInfo.organizationRequired",
                        ),
                      })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="TATO_NET">
                        {t("forms.builder.basicInfo.organizationTatoNet")}
                      </option>
                      <option value="FCM">
                        {t("forms.builder.basicInfo.organizationFcm")}
                      </option>
                    </select>
                    {errors.organization && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.organization.message}
                      </p>
                    )}
                  </div>

                  {/* Rules Checkbox Configuration */}
                  <div>
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {t("forms.builder.basicInfo.rulesLabel")}
                      </label>
                      <input
                        type="text"
                        {...register("rules_label")}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder={t(
                          "forms.builder.basicInfo.rulesLabelPlaceholder"
                        )}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t("forms.builder.basicInfo.rulesLabelHelp")}
                      </p>
                    </div> */}

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {t("forms.builder.basicInfo.rulesUrl")}
                      </label>
                      <input
                        type="text"
                        {...register("rules_url", {
                          validate: (value, formValues) => {
                            // If rules_label is set, rules_url is required
                            if (
                              formValues.rules_label &&
                              formValues.rules_label.trim() !== "" &&
                              (!value || value.trim() === "")
                            ) {
                              return "Rules URL is required when label is set";
                            }
                            return true;
                          },
                        })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder={t(
                          "forms.builder.basicInfo.rulesUrlPlaceholder",
                        )}
                      />
                      {errors.rules_url && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.rules_url.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {t("forms.builder.basicInfo.rulesUrlHelp")}
                      </p>
                    </div>
                  </div>

                  {/* Scholarship Toggle */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        {...register("scholarship_enabled")}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        {t("forms.builder.basicInfo.enableScholarship")}
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      {t(
                        "forms.builder.basicInfo.enableScholarshipDescription",
                      )}
                    </p>
                  </div>

                  {/* Custom Price Toggle */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        {...register("custom_price_enabled")}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        {t("forms.builder.basicInfo.enableCustomPrice")}
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      {t(
                        "forms.builder.basicInfo.enableCustomPriceDescription",
                      )}
                    </p>
                  </div>

                  {/* Promo Codes Toggle */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        {...register("promo_codes_enabled")}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        {t("forms.builder.basicInfo.enablePromoCodes")}
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      {t("forms.builder.basicInfo.enablePromoCodesDescription")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200"></div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Pipedrive</h3>
                {/* Product Search */}
                <div className="relative product-search-container">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("forms.builder.integration.searchPipedriveProduct")}
                  </label>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      value={productSearchTerm}
                      onChange={(e) => {
                        setProductSearchTerm(e.target.value);
                        setShowProductDropdown(e.target.value.length > 0);
                      }}
                      onFocus={() =>
                        setShowProductDropdown(productSearchTerm.length > 0)
                      }
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm pr-10"
                      placeholder={t(
                        "forms.builder.integration.searchPlaceholder",
                      )}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Product Dropdown */}
                  {showProductDropdown && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg border border-gray-300 rounded-md max-h-60 overflow-auto">
                      {searchResults.length > 0 ? (
                        searchResults
                          .sort(
                            (a, b) =>
                              (b.result_score || 0) - (a.result_score || 0),
                          )
                          .map((product) => (
                            <button
                              key={product.id}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleProductSelect(product);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer"
                            >
                              <div className="font-medium text-gray-900">
                                {product.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {product.id} | Score: {product.result_score}
                              </div>
                            </button>
                          ))
                      ) : productSearchTerm.length > 0 ? (
                        <div className="px-3 py-2 text-gray-500 text-sm">
                          {t("forms.builder.integration.noProductsFound", {
                            term: productSearchTerm,
                          })}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {selectedProduct && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-green-800">
                            ✅ {t("forms.builder.integration.selectedProduct")}{" "}
                            <strong>{selectedProduct.name}</strong>
                          </div>
                          <div className="text-xs text-green-600">
                            {t("forms.builder.integration.pipedriveId")}{" "}
                            {selectedProduct.id} |{" "}
                            {t("forms.builder.integration.availableVariations")}{" "}
                            {availableVariations.length}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedProduct(null);
                            setProductSearchTerm("");
                            setAvailableVariations([]);
                            setShowVariationSelector(false);
                            setValue("pipedrive_product_id", 0);
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          {t("forms.builder.integration.clear")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Product Variations */}
              <div>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-lm font-medium text-gray-700">
                      {t("forms.builder.variations.title")}
                    </label>
                    {selectedProduct && (
                      <button
                        type="button"
                        onClick={() =>
                          setShowVariationSelector(!showVariationSelector)
                        }
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <PlusIcon className="w-4 h-4 mr-1" />
                        {t("forms.builder.variations.addVariation")}
                      </button>
                    )}
                  </div>

                  {selectedProduct && (
                    <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <strong>Product:</strong> {selectedProduct.name} |
                          <strong>
                            {" "}
                            {t("forms.builder.integration.availableVariations")}
                          </strong>{" "}
                          {availableVariations.length} |
                          <strong>
                            {" "}
                            {t("forms.builder.integration.addedVariations")}
                          </strong>{" "}
                          {variationFields.length}
                        </div>
                      </div>
                    </div>
                  )}

                  {!selectedProduct && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">
                        ⚠️ {t("forms.builder.integration.selectProductFirst")}
                      </p>
                    </div>
                  )}

                  {/* Variation Selector Modal/Dropdown */}
                  {showVariationSelector && selectedProduct && (
                    <div className="mb-4 p-4 bg-white border border-gray-300 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          {t("forms.builder.variations.chooseVariationType")}
                        </h4>
                        <button
                          type="button"
                          onClick={() => setShowVariationSelector(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <span className="sr-only">Close</span>✕
                        </button>
                      </div>

                      <div className="space-y-2">
                        {/* Base product option - show if not already added */}
                        {!variationFields.some(
                          (field) => field.pipedrive_variation_id === -1,
                        ) && (
                          <button
                            type="button"
                            onClick={() => {
                              appendVariation({
                                name: `${selectedProduct.name}`,
                                stripe_price_id: "",
                                pipedrive_variation_id: -1,
                                price: selectedProduct.prices?.[0]?.price || 0,
                                expires_at: null,
                              });
                              setShowVariationSelector(false);
                            }}
                            className="w-full text-left px-3 py-2 border border-blue-200 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <div className="font-medium text-blue-900">
                              {selectedProduct.name}
                            </div>
                            <div className="text-sm text-blue-600">
                              {t("forms.builder.variations.baseProduct")}
                            </div>
                          </button>
                        )}

                        {/* Available Pipedrive product variations */}
                        {availableVariations
                          .filter(
                            (variation) =>
                              !variationFields.some(
                                (field) =>
                                  field.pipedrive_variation_id === variation.id,
                              ),
                          )
                          .map((variation) => (
                            <button
                              key={variation.id}
                              type="button"
                              onClick={() => {
                                handleAddVariation(variation);
                                setShowVariationSelector(false);
                              }}
                              className="w-full text-left px-3 py-2 border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <div className="font-medium text-gray-900">
                                {variation.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {t(
                                  "forms.builder.variations.pipedriveVariation",
                                )}{" "}
                                {variation.prices?.[0]
                                  ? `- ${variation.prices[0].price} zł`
                                  : ""}
                              </div>
                            </button>
                          ))}

                        {/* Show message when no more variations available */}
                        {availableVariations.filter(
                          (variation) =>
                            !variationFields.some(
                              (field) =>
                                field.pipedrive_variation_id === variation.id,
                            ),
                        ).length === 0 &&
                          variationFields.some(
                            (field) => field.pipedrive_variation_id === -1,
                          ) && (
                            <p className="text-sm text-gray-500 text-center py-2">
                              {t("forms.builder.variations.allVariationsAdded")}
                            </p>
                          )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {variationFields.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
                      <p className="text-gray-500 text-sm">
                        {selectedProduct
                          ? t("forms.builder.variations.noVariationsYet")
                          : t(
                              "forms.builder.variations.selectProductForVariations",
                            )}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Warning when base variation is missing */}
                      {selectedProduct &&
                        !variationFields.some(
                          (field) => field.pipedrive_variation_id === -1,
                        ) && (
                          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <div className="flex items-center">
                              <span className="text-orange-600 mr-2">⚠️</span>
                              <div>
                                <p className="text-sm font-medium text-orange-800">
                                  {t(
                                    "forms.builder.variations.baseVariationMissingWarning",
                                  )}
                                </p>
                                <p className="text-xs text-orange-600">
                                  {t(
                                    "forms.builder.variations.baseVariationGuidance",
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                      {variationFields.map((field, index) => (
                        <div
                          key={field.id}
                          className={`border rounded-lg p-4 ${
                            field.pipedrive_variation_id === -1
                              ? "border-blue-200 bg-blue-50"
                              : "border-gray-200"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">
                                {t("forms.builder.variations.variation")}{" "}
                                {index + 1}
                                {field.pipedrive_variation_id === -1 && (
                                  <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                    {t("forms.builder.variations.baseBadge")}
                                  </span>
                                )}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {field.pipedrive_variation_id === -1
                                  ? t(
                                      "forms.builder.variations.typeBaseProduct",
                                    )
                                  : t(
                                      "forms.builder.variations.typeProductVariation",
                                    )}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeVariation(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                {t("forms.builder.variations.name")}
                              </label>
                              <input
                                type="text"
                                {...register(`variations.${index}.name`, {
                                  required: t(
                                    "forms.builder.variations.nameRequired",
                                  ),
                                })}
                                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                placeholder={
                                  field.pipedrive_variation_id !== 0
                                    ? t(
                                        "forms.builder.variations.autoFilledPlaceholder",
                                      )
                                    : t(
                                        "forms.builder.variations.namePlaceholder",
                                      )
                                }
                              />
                              {field.pipedrive_variation_id !== 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {t("forms.builder.variations.nameAutoFilled")}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                {t("forms.builder.variations.stripePriceId")}
                              </label>
                              <input
                                type="text"
                                {...register(
                                  `variations.${index}.stripe_price_id`,
                                  {
                                    required: t(
                                      "forms.builder.variations.stripePriceIdRequired",
                                    ),
                                    pattern: {
                                      value: /^price_[A-Za-z0-9]+$/,
                                      message: t(
                                        "forms.builder.variations.stripePriceIdInvalid",
                                      ),
                                    },
                                  },
                                )}
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                placeholder="price_xxxxxxxxxx"
                              />
                              {errors.variations &&
                                errors.variations[index]?.stripe_price_id && (
                                  <p className="text-xs text-red-600 mt-1">
                                    {
                                      errors.variations[index]?.stripe_price_id
                                        ?.message
                                    }
                                  </p>
                                )}
                            </div>
                            {!watchedData.custom_price_enabled && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  {t("forms.builder.variations.price")}
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  {...register(`variations.${index}.price`, {
                                    required: "Price is required",
                                    min: 0,
                                  })}
                                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                  placeholder={
                                    field.pipedrive_variation_id !== 0
                                      ? t(
                                          "forms.builder.variations.autoFilledPlaceholder",
                                        )
                                      : "29.99"
                                  }
                                />
                                {field.pipedrive_variation_id !== 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {t(
                                      "forms.builder.variations.priceAutoFilled",
                                    )}
                                  </p>
                                )}
                              </div>
                            )}
                            {/* Expiration settings */}
                            <div>
                              <label className="flex items-center mb-3">
                                <input
                                  type="checkbox"
                                  checked={
                                    !!watch(`variations.${index}.expires_at`)
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      // Set to current datetime when enabling
                                      const now = new Date();
                                      setValue(
                                        `variations.${index}.expires_at`,
                                        now,
                                      );
                                    } else {
                                      // Clear when disabling
                                      setValue(
                                        `variations.${index}.expires_at`,
                                        null,
                                      );
                                    }
                                  }}
                                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-700">
                                  {t(
                                    "forms.builder.variations.enableExpiration",
                                  )}
                                </span>
                              </label>

                              {watch(`variations.${index}.expires_at`) && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t("forms.builder.variations.expiresAt")}
                                  </label>
                                  <input
                                    type="datetime-local"
                                    value={
                                      watch(
                                        `variations.${index}.expires_at`,
                                      ) instanceof Date
                                        ? dateToLocalDateTimeString(
                                            watch(
                                              `variations.${index}.expires_at`,
                                            ) as Date,
                                          )
                                        : ""
                                    }
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        setValue(
                                          `variations.${index}.expires_at`,
                                          localDateTimeStringToDate(
                                            e.target.value,
                                          ),
                                        );
                                      }
                                    }}
                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    {t(
                                      "forms.builder.variations.expiresAtDescription",
                                    )}
                                  </p>
                                </div>
                              )}
                            </div>
                            {/* Hidden field for pipedrive_variation_id */}
                            <input
                              type="hidden"
                              {...register(
                                `variations.${index}.pipedrive_variation_id`,
                              )}
                            />
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
              {/* Custom Fields */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-lm font-medium text-gray-700">
                    {t("forms.builder.customFields.title")}
                  </label>
                  <button
                    type="button"
                    onClick={handleAddCustomField}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    {t("forms.builder.customFields.addCustomField")}
                  </button>
                </div>

                <div className="space-y-4">
                  {customFieldsArray.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
                      <p className="text-gray-500 text-sm">
                        {t("forms.builder.customFields.noCustomFields")}
                      </p>
                    </div>
                  ) : (
                    customFieldsArray.map((field, index) => (
                      <div
                        key={field.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-900">
                            {t("forms.builder.customFields.customField")}{" "}
                            {index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeCustomField(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              {t(
                                "forms.builder.customFields.fieldLabelDisplay",
                              )}
                            </label>
                            <input
                              type="text"
                              {...register(`custom_fields.${index}.label`, {
                                required: t(
                                  "forms.builder.customFields.fieldLabelRequired",
                                ),
                              })}
                              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              placeholder={t(
                                "forms.builder.customFields.fieldLabelDisplayPlaceholder",
                              )}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {t(
                                "forms.builder.customFields.fieldLabelDisplayHelp",
                              )}
                            </p>
                            {errors.custom_fields?.[index]?.label && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.custom_fields[index]?.label?.message}
                              </p>
                            )}
                          </div>

                          {/* Hidden input to maintain the generated field name */}
                          <input
                            type="hidden"
                            {...register(`custom_fields.${index}.name`)}
                          />

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              {t(
                                "forms.builder.customFields.pipedriveCustomFieldId",
                              )}
                            </label>
                            <Controller
                              name={`custom_fields.${index}.pipedrive_deal_field_code`}
                              control={control}
                              render={({ field }) => (
                                <select
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    field.onChange(e.target.value || "");
                                  }}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                >
                                  <option value="">
                                    {t(
                                      "forms.builder.customFields.selectPipedriveField",
                                    )}
                                  </option>
                                  {dealFields.map((dealField) => (
                                    <option
                                      key={dealField.code}
                                      value={dealField.code}
                                    >
                                      {dealField.name} ({dealField.type})
                                    </option>
                                  ))}
                                </select>
                              )}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {t(
                                "forms.builder.customFields.pipedriveCustomFieldIdHelp",
                              )}
                            </p>
                          </div>

                          <div className="flex items-center">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                {...register(`custom_fields.${index}.required`)}
                                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                              />
                              <span className="ml-2 text-sm font-medium text-gray-700">
                                {t("forms.builder.customFields.requiredField")}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200"></div>

              {/* Custom CSS */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t("forms.builder.customCss.title")}
                </h3>
                <textarea
                  {...register("css")}
                  rows={10}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-mono text-xs"
                  placeholder={t("forms.builder.customCss.placeholder")}
                />
              </div>
              {/* Submit Button */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate("/forms")}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {t("forms.builder.actions.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={
                    createFormMutation.isLoading || updateFormMutation.isLoading
                  }
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {createFormMutation.isLoading || updateFormMutation.isLoading
                    ? isEditMode
                      ? t("forms.builder.actions.updating") || "Updating..."
                      : t("forms.builder.actions.creating")
                    : isEditMode
                      ? t("forms.builder.actions.updateForm") || "Update Form"
                      : t("forms.builder.actions.createForm")}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t("forms.builder.preview.title")}
            </h3>
            <iframe
              srcDoc={previewHtml}
              className="w-full rounded-md"
              style={{ height: "1000px" }}
              title="Form Preview"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
